import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const sessionId = params.get("session_id");
  const videoId = params.get("video_id");
  const [state, setState] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) return setState("error");
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setState("paid");
        if (["expired", "failed"].includes(data.payment_status)) return setState("error");
      } catch {
        return setState("error");
      }
      if (attempts.current >= 10) return setState("timeout");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="bg-luxe min-h-screen">
      <Header />
      <div className="max-w-md mx-auto px-6 pt-40 pb-24 text-center">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="w-10 h-10 animate-spin text-[#D4AF37] mx-auto" />
            <h1 className="font-display text-2xl text-[#F5F3EE] mt-8">Vérification du paiement…</h1>
            <p className="mt-3 text-sm text-[#8F8F8F]">Un instant, nous confirmons votre achat.</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="payment-success">
            <CheckCircle2 className="w-12 h-12 text-[#D4AF37] mx-auto" />
            <h1 className="font-display text-3xl text-[#F5F3EE] mt-8">Paiement confirmé</h1>
            <p className="mt-4 text-sm text-[#B0B0B0]">
              Votre vidéo est débloquée.
              {!user && " Créez un compte (ou connectez-vous) avec l'email utilisé lors du paiement pour la regarder."}
            </p>
            <div className="mt-10 flex flex-col gap-3">
              {user ? (
                <Link
                  to={videoId ? `/video/${videoId}` : "/mes-videos"}
                  data-testid="watch-video-link"
                  className="bg-[#D4AF37] text-[#0A0A0A] font-semibold py-4 text-sm uppercase tracking-widest hover:bg-[#C9A227] transition-colors"
                >
                  Regarder ma vidéo
                </Link>
              ) : (
                <Link
                  to="/inscription"
                  data-testid="create-account-link"
                  className="bg-[#D4AF37] text-[#0A0A0A] font-semibold py-4 text-sm uppercase tracking-widest hover:bg-[#C9A227] transition-colors"
                >
                  Créer mon compte
                </Link>
              )}
              <Link to="/" data-testid="back-home-link" className="text-sm text-[#8F8F8F] hover:text-[#D4AF37] transition-colors py-2">
                Retour au catalogue
              </Link>
            </div>
          </div>
        )}
        {(state === "error" || state === "timeout") && (
          <div data-testid="payment-error">
            <XCircle className="w-12 h-12 text-red-500 mx-auto" />
            <h1 className="font-display text-2xl text-[#F5F3EE] mt-8">
              {state === "timeout" ? "Vérification en attente" : "Paiement non confirmé"}
            </h1>
            <p className="mt-4 text-sm text-[#8F8F8F]">
              {state === "timeout"
                ? "La confirmation prend plus de temps que prévu. Si vous avez payé, votre accès sera débloqué automatiquement d'ici quelques minutes."
                : "Le paiement n'a pas pu être confirmé. Aucun montant n'a été débité si vous avez annulé."}
            </p>
            <Link
              to={videoId ? `/video/${videoId}` : "/"}
              data-testid="retry-link"
              className="inline-block mt-10 border border-[#D4AF37] text-[#D4AF37] px-8 py-3.5 text-sm uppercase tracking-widest hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors"
            >
              Revenir à la vidéo
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
